/**
 * MA-ARCH-130: Profit Paths Page
 * MA-UI-071-A: Legacy modal removed - all creation/editing via Builder
 * MA-UI-072: Guided Entry Layer - Strategic initialization before builder
 * 
 * Profit Path = Strategy (message + funnel + script + outcomes)
 * Campaign = Traffic source (Meta, Google, etc.)
 * 
 * These are separate entities - this page manages the strategic layer.
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/contexts/TenantContext";  // MA-PHASE11A-P0-AUDIT-005
import useActivation from "@/hooks/useActivation";
import { useHasModule, useModuleAccessLevel } from "@/hooks/usePermissions";
import {
  Plus,
  Search,
  DollarSign,
  Users,
  MoreHorizontal,
  Edit,
  Pause,
  Play,
  Archive,
  ChevronRight,
  ChevronLeft,
  Route,
  Settings2,
  ChevronDown,
  ArrowDownAZ,
  ArrowUpAZ,
  ArrowUpDown,
  ListFilter,
  Target,
  BookOpen,
  Zap,
  Thermometer,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";
import InCardFilterPanel from "@/components/leads/InCardFilterPanel";
import { api } from "@/lib/api";
import { toast } from "sonner";
// MA-AN-131: Profit Path Performance Analytics
import ProfitPathPerformance from "@/components/warroom/ProfitPathPerformance";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3 } from "lucide-react";

// =============================================================================
// MA-UI-072: GUIDED ENTRY LAYER COMPONENT
// MA-UI-073: ENFORCED CONSTRAINTS (V1 Implementation Guardrails)
// - Max 4 questions (3 required + 1 optional)
// - NO SCROLLING - must fit viewport
// - NO ADVANCED LOGIC - flat, static input layer
// - NO EXTRA FIELDS - no campaign, channel, workspace, etc.
// - NO FEATURE CREEP - minimal design
// =============================================================================

const ENTRY_LAYER_OPTIONS = {
  goal: [
    { value: "generate_leads", label: "Leads", description: "Capture interest", icon: Users },
    { value: "book_appointments", label: "Appointments", description: "Schedule calls", icon: Target },
    { value: "close_sales", label: "Sales", description: "Drive purchases", icon: DollarSign },
  ],
  offer_type: [
    { value: "education_review", label: "Education", description: "Insight-first", icon: BookOpen },
    { value: "consultation", label: "Consultation", description: "Advisory", icon: Users },
    { value: "direct_sale", label: "Direct Sale", description: "Action-first", icon: DollarSign },
  ],
  conversion_style: [
    { value: "educate_first", label: "Educate First", description: "Build trust first", icon: BookOpen },
    { value: "fast_conversion", label: "Fast Conversion", description: "Quick action", icon: Zap },
  ],
  traffic_temperature: [
    { value: "cold", label: "Cold", color: "text-blue-400" },
    { value: "warm", label: "Warm", color: "text-amber-400" },
    { value: "hot", label: "Hot", color: "text-red-400" },
  ],
};

function EntryLayerModal({ open, onOpenChange, onSubmit }) {
  const [entryProfile, setEntryProfile] = useState({
    goal: null,
    offer_type: null,
    conversion_style: null,
    traffic_temperature: null,
  });

  const isValid = entryProfile.goal && entryProfile.offer_type && entryProfile.conversion_style;

  const handleSubmit = () => {
    if (isValid) {
      onSubmit(entryProfile);
    }
  };

  const handleOptionSelect = (field, value) => {
    setEntryProfile(prev => ({ ...prev, [field]: value }));
  };

  // Reset on close
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setEntryProfile({
        goal: null,
        offer_type: null,
        conversion_style: null,
        traffic_temperature: null,
      });
    }
    onOpenChange(newOpen);
  };

  // MA-UI-073: Compact option card - no scroll design
  const OptionCard = ({ option, field, isSelected, hasIcon = true }) => {
    const Icon = option.icon;
    return (
      <button
        onClick={() => handleOptionSelect(field, option.value)}
        className={`
          px-3 py-2.5 rounded-lg border text-left transition-all
          ${isSelected 
            ? "bg-indigo-600/20 border-indigo-500 ring-1 ring-indigo-500/50" 
            : "bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800"
          }
        `}
        data-testid={`entry-${field}-${option.value}`}
      >
        <div className="flex items-center gap-2">
          {hasIcon && Icon && <Icon className={`h-4 w-4 ${isSelected ? "text-indigo-400" : "text-slate-400"}`} />}
          {!hasIcon && <Thermometer className={`h-4 w-4 ${isSelected ? "text-indigo-400" : option.color}`} />}
          <span className={`font-medium text-sm ${isSelected ? "text-white" : "text-slate-200"}`}>
            {option.label}
          </span>
        </div>
        {option.description && (
          <div className="text-[11px] text-slate-500 mt-0.5 ml-6">{option.description}</div>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl bg-slate-900 border-slate-700 text-white p-0">
        {/* Header - Compact */}
        <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-b border-slate-700 px-5 py-4">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-400" />
              Initialize Profit Path
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Quick setup to pre-configure your builder.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Content - NO SCROLL, compact layout */}
        <div className="px-5 pt-4 pb-6 space-y-4">
          {/* Q1: Primary Goal */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-white">Primary Goal</Label>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-indigo-500/50 text-indigo-400">Required</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {ENTRY_LAYER_OPTIONS.goal.map((option) => (
                <OptionCard 
                  key={option.value} 
                  option={option} 
                  field="goal"
                  isSelected={entryProfile.goal === option.value} 
                />
              ))}
            </div>
          </div>

          {/* Q2: Offer Type */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-white">Offer Type</Label>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-indigo-500/50 text-indigo-400">Required</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {ENTRY_LAYER_OPTIONS.offer_type.map((option) => (
                <OptionCard 
                  key={option.value} 
                  option={option} 
                  field="offer_type"
                  isSelected={entryProfile.offer_type === option.value} 
                />
              ))}
            </div>
          </div>

          {/* Q3: Conversion Style */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-white">Conversion Style</Label>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-indigo-500/50 text-indigo-400">Required</Badge>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {ENTRY_LAYER_OPTIONS.conversion_style.map((option) => (
                <OptionCard 
                  key={option.value} 
                  option={option} 
                  field="conversion_style"
                  isSelected={entryProfile.conversion_style === option.value} 
                />
              ))}
            </div>
          </div>

          {/* Q4: Traffic Temperature (Optional) - Inline compact */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-white">Audience Temperature</Label>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-slate-600 text-slate-400">Optional</Badge>
            </div>
            <div className="flex gap-2">
              {ENTRY_LAYER_OPTIONS.traffic_temperature.map((option) => (
                <OptionCard 
                  key={option.value} 
                  option={option} 
                  field="traffic_temperature"
                  isSelected={entryProfile.traffic_temperature === option.value}
                  hasIcon={false}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer - Compact */}
        <DialogFooter className="border-t border-slate-700 px-5 py-3 bg-slate-900/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenChange(false)}
            className="text-slate-400 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!isValid}
            className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
            data-testid="entry-build-btn"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Build Profit Path
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  { value: "active", label: "Active", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  { value: "paused", label: "Paused", color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  { value: "archived", label: "Archived", color: "bg-red-500/20 text-red-300 border-red-500/30" },
];

const MESSAGE_CATEGORY_OPTIONS = [
  { value: "cost_savings", label: "Cost Savings" },
  { value: "income", label: "Income" },
  { value: "protection", label: "Protection" },
  { value: "growth", label: "Growth" },
  { value: "convenience", label: "Convenience" },
  { value: "certainty", label: "Certainty" },
  { value: "opportunity", label: "Opportunity" },
  { value: "relief", label: "Relief" },
  { value: "speed", label: "Speed" },
  { value: "status", label: "Status" },
  { value: "other", label: "Other" },
];

// =============================================================================
// API HOOKS
// =============================================================================

/**
 * MA-PHASE11A-P0-AUDIT-005: React Query hook includes selectedTenantId in key.
 * MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only enable when selectedTenantId exists.
 */
function useProfitPaths({ page, limit, status, search, selectedTenantId }) {
  return useQuery({
    queryKey: ["profit-paths", selectedTenantId, page, limit, status, search],  // MA-PHASE11A: Include selectedTenantId
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (status && status !== "all") params.append("status", status);
      if (search) params.append("search", search);
      const res = await api.get(`/profit-paths?${params}`);
      return res.data;
    },
    staleTime: 30000,
    enabled: !!selectedTenantId,  // MA-PHASE11A: Prevent premature API calls
  });
}

function useCreateProfitPath() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await api.post("/profit-paths", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profit-paths"] });
      toast.success("Profit path created");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to create profit path");
    },
  });
}

function useUpdateProfitPath() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await api.patch(`/profit-paths/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profit-paths"] });
      toast.success("Profit path updated");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to update profit path");
    },
  });
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(value) {
  if (value === null || value === undefined) return "$0";
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function getStatusBadge(status) {
  const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <Badge variant="outline" className={config.color}>
      {config.label}
    </Badge>
  );
}

function normalizeMessageVariant(value) {
  if (!value) return "";
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

// =============================================================================
// MA-UI-071-A: Legacy Modal and Wizard REMOVED
// All profit path creation/editing now goes through ProfitPathBuilder
// =============================================================================

// Vertical to business type mapping (still used for data normalization)
const VERTICAL_TO_BUSINESS_TYPE = {
  'medicare': 'medicare',
  'mortgage': 'mortgage_protection',
  'mortgage_protection': 'mortgage_protection',
  'life': 'iul',
  'life_insurance': 'iul',
  'iul': 'iul',
  'final_expense': 'final_expense',
  'health': 'health_insurance',
  'health_insurance': 'health_insurance',
  'p_and_c': 'p_and_c',
  'property_casualty': 'p_and_c',
  'financial': 'financial_services',
  'financial_services': 'financial_services',
  'real_estate': 'real_estate',
};


// MA-UI-071-A: Legacy ProfitPathModal REMOVED
// All profit path creation/editing now goes through ProfitPathBuilder at /app/profit-paths/:id/builder
// For new paths: /app/profit-paths/new/builder


// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

export default function ProfitPathsPage() {
  const navigate = useNavigate();
  const { isActivated } = useActivation();
  
  // MA-RBAC-BATCH3-INFRA-115A: Use profit_paths module with full access level for builder actions
  const { hasFullAccess: canCreateProfitPaths } = useModuleAccessLevel("profit_paths");
  
  // MA-PHASE11A-P0-AUDIT-005: Get selectedTenantId for proper cache isolation
  const { selectedTenantId } = useTenant();
  
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // MA-UI-072: Entry Layer modal state
  const [entryLayerOpen, setEntryLayerOpen] = useState(false);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("profit_paths_filters_open");
      return stored !== null ? stored === "true" : true;
    } catch {
      return true;
    }
  });
  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    message: true,
    vertical: true,
    funnels: true,
    campaigns: true,
    leads: true,
    revenue: true,
    status: true,
  });

  // Sorting handlers
  const applySort = (field, dir) => {
    setSortBy(field);
    setSortDir(dir);
    setPage(1);
  };

  const clearSort = () => {
    setSortBy(null);
    setSortDir("desc");
    setPage(1);
  };

  // Persist filter panel open state
  useEffect(() => {
    try {
      localStorage.setItem("profit_paths_filters_open", String(filtersOpen));
    } catch {
      // ignore
    }
  }, [filtersOpen]);

  // Define available columns
  const availableColumns = [
    { key: "name", label: "Name" },
    { key: "message", label: "Message" },
    { key: "vertical", label: "Vertical" },
    { key: "funnels", label: "Funnels" },
    { key: "campaigns", label: "Campaigns" },
    { key: "leads", label: "Leads" },
    { key: "revenue", label: "Revenue" },
    { key: "status", label: "Status" },
  ];

  // Define filter fields for the filter panel (alphabetical order)
  const filterFieldDefs = [
    { key: "message_category", label: "Message Category", type: "select" },
    { key: "message_variant", label: "Message Variant", type: "text" },
    { key: "name", label: "Name", type: "text" },
    { key: "status", label: "Status", type: "select" },
    { key: "vertical", label: "Vertical", type: "select" },
  ];

  // Check if any filters are active
  const filtersActive = useMemo(() => {
    return Object.values(filtersApplied).some((f) => f?.enabled);
  }, [filtersApplied]);

  // Handler to apply filters from draft
  const applyFilters = () => {
    setFiltersApplied(filtersDraft);
  };

  // Handler to clear all filters
  const clearFilters = () => {
    setFiltersDraft({});
    setFiltersApplied({});
  };

  // Build query params from applied filters (for server-side filtering - status only)
  const queryFilters = useMemo(() => {
    const params = {};
    Object.entries(filtersApplied).forEach(([key, filter]) => {
      if (filter?.enabled) {
        if (key === "status" && filter.values?.length > 0) {
          // Use first value for API query (API only supports single status)
          params.status = filter.values[0];
        }
      }
    });
    return params;
  }, [filtersApplied]);

  const query = useProfitPaths({ 
    page, 
    limit, 
    status: queryFilters.status || "all", 
    search: debouncedSearch,
    selectedTenantId,  // MA-PHASE11A-P0-AUDIT-005: Include for query key isolation
  });
  const createMutation = useCreateProfitPath();
  const updateMutation = useUpdateProfitPath();

  // Build filter options from the data (must be after query is defined)
  const filterOptionsByField = useMemo(() => {
    // Get unique verticals from the data
    const uniqueVerticals = [...new Set(
      (query.data?.items || [])
        .map(p => p.vertical)
        .filter(Boolean)
    )].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
    
    return {
      status: STATUS_OPTIONS.map((opt) => opt.value),
      message_category: MESSAGE_CATEGORY_OPTIONS.map((opt) => opt.value),
      vertical: uniqueVerticals.map(v => v.value),
    };
  }, [query.data?.items]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // MA-UI-071-A: Handle edit query param - navigate to builder instead of modal
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) {
      // Navigate to builder for editing
      navigate(`/app/profit-paths/${editId}/builder`);
      // Clear the query param
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, navigate, setSearchParams]);

  // MA-UI-072: Show Entry Layer modal for new paths
  // MA-UI-078: Gate on activation status
  const handleCreate = () => {
    if (!isActivated) {
      navigate("/activation");
      return;
    }
    setEntryLayerOpen(true);
  };

  // MA-UI-072: Handle Entry Layer submission - navigate to builder with entry_profile
  const handleEntryLayerSubmit = (entryProfile) => {
    setEntryLayerOpen(false);
    // Navigate to builder with entry_profile in state
    navigate("/app/profit-paths/new/builder", { state: { entry_profile: entryProfile } });
  };

  // MA-UI-071-A: Navigate to Builder for editing (modal removed)
  const handleEdit = (path) => {
    navigate(`/app/profit-paths/${path.id}/builder`);
  };

  const handleStatusChange = async (path, newStatus) => {
    await updateMutation.mutateAsync({ id: path.id, data: { status: newStatus } });
  };

  // Selection helpers
  const anySelected = selected.size > 0;
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rawPaths = query.data?.items || [];
  const total = query.data?.total || 0;

  // Client-side filtering for all filter fields
  const paths = useMemo(() => {
    if (!filtersActive) return rawPaths;
    
    return rawPaths.filter((path) => {
      // Check each enabled filter
      for (const [key, filter] of Object.entries(filtersApplied)) {
        if (!filter?.enabled) continue;
        
        const operator = filter.operator || "is";
        const filterValues = filter.values || [];
        const filterValue = filter.value || "";
        
        // Get the value from the path object
        let pathValue = path[key];
        
        // Handle special cases for display fields
        if (key === "message") {
          // Message filter checks both category and variant
          const combined = `${path.message_category || ""} ${path.message_variant || ""}`.toLowerCase();
          if (operator === "contains" && filterValue) {
            if (!combined.includes(filterValue.toLowerCase())) return false;
          }
          continue;
        }
        
        // Status filter - multi-select
        if (key === "status" && filterValues.length > 0) {
          if (operator === "is" && !filterValues.includes(pathValue)) return false;
          if (operator === "is_not" && filterValues.includes(pathValue)) return false;
          continue;
        }
        
        // Message category filter - multi-select
        if (key === "message_category" && filterValues.length > 0) {
          if (operator === "is" && !filterValues.includes(pathValue)) return false;
          if (operator === "is_not" && filterValues.includes(pathValue)) return false;
          continue;
        }
        
        // Vertical filter - multi-select or text
        if (key === "vertical") {
          if (filterValues.length > 0) {
            const lowerPathValue = (pathValue || "").toLowerCase();
            const lowerFilterValues = filterValues.map(v => v.toLowerCase());
            if (operator === "is" && !lowerFilterValues.includes(lowerPathValue)) return false;
            if (operator === "is_not" && lowerFilterValues.includes(lowerPathValue)) return false;
          }
          continue;
        }
        
        // Text filters (name, message_variant)
        if (filterValue) {
          const strValue = String(pathValue || "").toLowerCase();
          const strFilter = filterValue.toLowerCase();
          
          switch (operator) {
            case "is":
              if (strValue !== strFilter) return false;
              break;
            case "isnt":
              if (strValue === strFilter) return false;
              break;
            case "contains":
              if (!strValue.includes(strFilter)) return false;
              break;
            case "not_contains":
              if (strValue.includes(strFilter)) return false;
              break;
            case "starts_with":
              if (!strValue.startsWith(strFilter)) return false;
              break;
            case "ends_with":
              if (!strValue.endsWith(strFilter)) return false;
              break;
            case "is_empty":
              if (strValue.length > 0) return false;
              break;
            case "is_not_empty":
              if (strValue.length === 0) return false;
              break;
          }
        }
        
        // Empty checks
        if (operator === "is_empty" && pathValue) return false;
        if (operator === "is_not_empty" && !pathValue) return false;
      }
      
      return true;
    });
  }, [rawPaths, filtersApplied, filtersActive]);

  // Apply client-side sorting
  const sortedPaths = useMemo(() => {
    if (!sortBy) return paths;
    
    return [...paths].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle special sortable fields
      if (sortBy === "leads") {
        aVal = a.lead_count || 0;
        bVal = b.lead_count || 0;
      } else if (sortBy === "revenue") {
        aVal = a.total_revenue || 0;
        bVal = b.total_revenue || 0;
      } else if (sortBy === "funnels") {
        aVal = a.funnel_count || 0;
        bVal = b.funnel_count || 0;
      } else if (sortBy === "campaigns") {
        aVal = a.campaign_count || 0;
        bVal = b.campaign_count || 0;
      } else if (sortBy === "message") {
        aVal = `${a.message_category || ""} ${a.message_variant || ""}`.toLowerCase();
        bVal = `${b.message_category || ""} ${b.message_variant || ""}`.toLowerCase();
      }
      
      // Handle string comparison
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDir === "asc" 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      // Handle numeric comparison
      if (sortDir === "asc") {
        return (aVal || 0) - (bVal || 0);
      }
      return (bVal || 0) - (aVal || 0);
    });
  }, [paths, sortBy, sortDir]);

  // Selection helpers for current page
  const allOnPageSelected = useMemo(() => 
    sortedPaths.length > 0 && sortedPaths.every((p) => selected.has(p.id)), 
    [sortedPaths, selected]
  );
  
  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        sortedPaths.forEach((p) => next.delete(p.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        sortedPaths.forEach((p) => next.add(p.id));
        return next;
      });
    }
  };

  // Get selected paths for bulk actions
  const selectedPaths = useMemo(() => 
    sortedPaths.filter((p) => selected.has(p.id)), 
    [sortedPaths, selected]
  );

  // Bulk status change handler
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedPaths.length === 0) return;
    await Promise.all(selectedPaths.map((p) => 
      updateMutation.mutateAsync({ id: p.id, data: { status: newStatus } })
    ));
    setSelected(new Set());
  };

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6" data-testid="profit-paths-page">
      {/* Top Control Bar - Tabs on left, Search + Columns + Button on right */}
      <Tabs defaultValue="overview" className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between gap-4 shrink-0">
          {/* Left side - Tabs */}
          <TabsList className="inline-flex h-auto w-fit max-w-full flex-wrap justify-start gap-1 bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20">
            <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100">
              <Route className="h-3.5 w-3.5 mr-1.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="performance" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100" data-testid="profit-path-performance-tab">
              <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
              Performance Analytics
            </TabsTrigger>
          </TabsList>
        
          {/* Right side - Actions, Search, Filter, Columns, Button */}
          <div className="flex items-center gap-2">
            {anySelected ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="profit-paths-bulk-actions"
                  >
                    Actions
                    <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="glass border-white/10 text-slate-100">
                  <DropdownMenuItem onClick={() => handleBulkStatusChange("active")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                    <Play className="mr-2 h-4 w-4 text-emerald-400" /> Activate
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkStatusChange("paused")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                    <Pause className="mr-2 h-4 w-4 text-amber-400" /> Pause
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkStatusChange("archived")} className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100">
                    <Archive className="mr-2 h-4 w-4" /> Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            <CollapsibleSearch
              value={search}
              onChange={setSearch}
              placeholder="Search profit paths..."
              width="w-[250px]"
              data-testid="profit-paths-search"
            />

            {/* Filter Button */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setFiltersOpen(!filtersOpen)}
              aria-label="Open filters"
              data-testid="profit-paths-filter-icon"
              className="relative"
            >
              <ListFilter className={`h-4 w-4 ${filtersOpen ? "text-indigo-400" : ""}`} />
              {filtersActive ? <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950" /> : null}
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setColumnsDialogOpen(true)}
              aria-label="Manage columns"
              data-testid="profit-paths-manage-columns"
            >
              <Settings2 className="h-4 w-4" />
            </Button>

            {/* MA-RBAC-BATCH3-INFRA-115A: Hide "+ New Profit Path" button for read-only users */}
            {canCreateProfitPaths && (
              <Button onClick={handleCreate} className="gap-2" data-testid="new-profit-path-btn">
                <Plus className="h-4 w-4" />
                New Profit Path
              </Button>
            )}
          </div>
        </div>

        {/* Overview Tab Content */}
        <TabsContent value="overview" className="flex-1 min-h-0 flex flex-col mt-4">
          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 shrink-0">
            <Card className="glass border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">Total Paths</p>
                    <p className="text-2xl font-bold text-white">{total}</p>
                  </div>
                  <Route className="h-8 w-8 text-blue-400/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">Active</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      {paths.filter((p) => p.status === "active").length}
                    </p>
                  </div>
                  <Play className="h-8 w-8 text-emerald-400/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">Total Leads</p>
                    <p className="text-2xl font-bold text-white">
                      {paths.reduce((sum, p) => sum + (p.lead_count || 0), 0)}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-400/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase">Total Revenue</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      {formatCurrency(paths.reduce((sum, p) => sum + (p.total_revenue || 0), 0))}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-emerald-400/50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Grid Layout with Filter Panel + Table */}
          <div
            className={
              "grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " +
              (filtersOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")
            }
          >
            {/* Filter Panel Card */}
            {filtersOpen && (
              <Card className="glass flex flex-col min-h-0 h-full" data-testid="profit-paths-filters-card">
                <CardContent className="flex-1 min-h-0 p-0">
                  <InCardFilterPanel
                    open={filtersOpen}
                    onOpenChange={setFiltersOpen}
                    title="Filter Profit Paths by"
                    fields={filterFieldDefs}
                    optionsByField={filterOptionsByField}
                    draft={filtersDraft}
                    onChangeDraft={setFiltersDraft}
                    onApply={applyFilters}
                    onClear={clearFilters}
                  />
                </CardContent>
              </Card>
            )}

            {/* Main Table Card */}
            <Card className="glass border-white/10 flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="profit-paths-list-card">
              <CardContent className="flex h-full flex-col p-0 min-h-0">
                {query.isLoading ? (
                  <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                    Loading…
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto min-h-0">
                    <Table>
                      <TableHeader className="relative">
                        <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-px translate-y-1 bg-white/10" />
                        <TableRow className="border-transparent hover:bg-transparent">
                          {/* Checkbox column */}
                          <TableHead className="text-slate-200/70 text-xs h-auto p-1 w-[50px]">
                            <div className="px-2 py-2 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={allOnPageSelected}
                                onCheckedChange={toggleAllOnPage}
                                className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                              />
                            </div>
                          </TableHead>
                          {columnVisibility.name && (
                            <TableHead className="text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-between gap-2">
                                <span>Name</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "name" ? " bg-white/10" : "")}
                                      aria-label="Sort Name"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("name", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("name", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                          {columnVisibility.message && (
                            <TableHead className="text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-between gap-2">
                                <span>Message</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "message" ? " bg-white/10" : "")}
                                      aria-label="Sort Message"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("message", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("message", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                          {columnVisibility.vertical && (
                            <TableHead className="text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-between gap-2">
                                <span>Vertical</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "vertical" ? " bg-white/10" : "")}
                                      aria-label="Sort Vertical"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("vertical", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("vertical", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                          {columnVisibility.funnels && (
                            <TableHead className="text-center text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-center gap-2">
                                <span>Funnels</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "funnels" ? " bg-white/10" : "")}
                                      aria-label="Sort Funnels"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("funnels", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("funnels", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                          {columnVisibility.campaigns && (
                            <TableHead className="text-center text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-center gap-2">
                                <span>Campaigns</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "campaigns" ? " bg-white/10" : "")}
                                      aria-label="Sort Campaigns"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("campaigns", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("campaigns", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                          {columnVisibility.leads && (
                            <TableHead className="text-center text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-center gap-2">
                                <span>Leads</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "leads" ? " bg-white/10" : "")}
                                      aria-label="Sort Leads"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("leads", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("leads", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                          {columnVisibility.revenue && (
                            <TableHead className="text-right text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-end gap-2">
                                <span>Revenue</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "revenue" ? " bg-white/10" : "")}
                                      aria-label="Sort Revenue"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("revenue", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("revenue", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                          {columnVisibility.status && (
                            <TableHead className="text-slate-200/70 text-xs h-auto p-1">
                              <div className="px-2 py-2 flex items-center justify-between gap-2">
                                <span>Status</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={"inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" + (sortBy === "status" ? " bg-white/10" : "")}
                                      aria-label="Sort Status"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem onClick={() => applySort("status", "asc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => applySort("status", "desc")} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={clearSort} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedPaths.length === 0 ? (
                          <TableRow className="border-transparent">
                            <TableCell colSpan={Object.values(columnVisibility).filter(Boolean).length + 2} className="py-16">
                              <div className="sticky left-1/2 -translate-x-1/2 w-max flex flex-col items-center justify-center">
                                <Route className="h-12 w-12 text-indigo-400 mb-3" />
                                <div className="text-sm text-slate-200/70">No profit paths found.</div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : sortedPaths.map((path) => (
                          <TableRow
                            key={path.id}
                            className="border-slate-800/50 hover:bg-slate-800/30 cursor-pointer"
                            onClick={() => navigate(`/app/profit-paths/${path.id}`)}
                            data-testid={`profit-path-row-${path.id}`}
                          >
                            {/* Checkbox cell */}
                            <TableCell className="w-[50px]" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                <Checkbox
                                  checked={selected.has(path.id)}
                                  onCheckedChange={() => toggleOne(path.id)}
                                  className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                />
                              </div>
                            </TableCell>
                            {columnVisibility.name && (
                              <TableCell>
                                <span className="font-medium text-white">{path.name}</span>
                              </TableCell>
                            )}
                            {columnVisibility.message && (
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="text-xs text-slate-400 capitalize">
                                    {path.message_category?.replace(/_/g, " ") || "—"}
                                  </span>
                                  <span className="text-slate-200 text-sm truncate max-w-[150px]">
                                    {path.message_variant || "—"}
                                  </span>
                                </div>
                              </TableCell>
                            )}
                            {columnVisibility.vertical && (
                              <TableCell className="text-slate-300 capitalize">
                                {path.vertical || "—"}
                              </TableCell>
                            )}
                            {columnVisibility.funnels && (
                              <TableCell className="text-center text-slate-300">
                                {path.funnel_count || 0}
                              </TableCell>
                            )}
                            {columnVisibility.campaigns && (
                              <TableCell className="text-center text-slate-300">
                                {path.campaign_count || 0}
                              </TableCell>
                            )}
                            {columnVisibility.leads && (
                              <TableCell className="text-center text-slate-300">
                                {path.lead_count || 0}
                              </TableCell>
                            )}
                            {columnVisibility.revenue && (
                              <TableCell className="text-right font-mono text-emerald-400">
                                {formatCurrency(path.total_revenue)}
                              </TableCell>
                            )}
                            {columnVisibility.status && (
                              <TableCell>{getStatusBadge(path.status)}</TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Footer with pagination */}
                <div className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
                    <div className="text-xs text-slate-200/70">
                      {anySelected ? `Selected: ${selected.size}` : `Showing ${sortedPaths.length} of ${total} profit paths`}
                      {!anySelected && filtersActive && <span className="ml-2 text-indigo-400">(filtered)</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {(page - 1) * limit > 0 && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </Button>
                      )}
                      {(page - 1) * limit + limit < total && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      )}
                      <Select
                        value={String(limit)}
                        onValueChange={(v) => {
                          const next = Number(v);
                          setLimit(next);
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="glass border-white/10">
                          {[10, 25, 50, 100].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n} / Page
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Performance Analytics Tab */}
        <TabsContent value="performance" data-testid="profit-path-performance-content">
          <ProfitPathPerformance 
            period="mtd"
            onPathClick={(pathId) => navigate(`/app/profit-paths/${pathId}`)}
          />
        </TabsContent>
      </Tabs>

      {/* MA-UI-071-A: Legacy Modal REMOVED - All creation/editing goes through Builder */}

      {/* MA-UI-072: Guided Entry Layer Modal */}
      <EntryLayerModal
        open={entryLayerOpen}
        onOpenChange={setEntryLayerOpen}
        onSubmit={handleEntryLayerSubmit}
      />

      {/* Manage Columns Dialog */}
      <Dialog open={columnsDialogOpen} onOpenChange={setColumnsDialogOpen}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Manage Columns</DialogTitle>
            <DialogDescription className="text-slate-400">
              Show or hide columns in the profit paths table
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {availableColumns.map((column) => (
              <div key={column.key} className="flex items-center space-x-3">
                <Checkbox
                  id={`column-${column.key}`}
                  checked={columnVisibility[column.key]}
                  onCheckedChange={(checked) =>
                    setColumnVisibility((prev) => ({
                      ...prev,
                      [column.key]: checked,
                    }))
                  }
                  className="border-slate-600 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                />
                <Label
                  htmlFor={`column-${column.key}`}
                  className="text-sm font-medium text-slate-200 cursor-pointer"
                >
                  {column.label}
                </Label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setColumnsDialogOpen(false)}
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
